import { useEffect, useState, useCallback } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { invalidateCached } from "../lib/sharedData";
import { useT } from "../lib/i18n";
import {
  Users, UserPlus, Shield, Eye, ChevronDown, Copy, Trash2, Mail,
  Crown, Loader2, X, Check, Info, AlertTriangle, Globe, Lock, Layers, Upload, Image as ImageIcon,
} from "lucide-react";

const ROLE_META = {
  admin:  { label: "Admin",  Icon: Crown,  tone: "bg-amber-50 text-amber-800 border-amber-200" },
  agent:  { label: "Agent",  Icon: Shield, tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  viewer: { label: "Viewer", Icon: Eye,    tone: "bg-slate-100 text-slate-700 border-slate-200" },
};

function fmtRelative(iso) {
  if (!iso) return "Never";
  const t = new Date(iso).getTime();
  const d = (Date.now() - t) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  if (d < 86400 * 7) return `${Math.floor(d / 86400)}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export default function SettingsTeam() {
  const [workspaces, setWorkspaces] = useState([]);
  const [active, setActive] = useState(null);  // {id, my_role, name, ...}
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [me, setMe] = useState(null);
  const [toast, setToast] = useState(null);

  const flash = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }, []);

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
    api.get("/workspaces/mine").then(({ data }) => {
      setWorkspaces(data || []);
      const primary = data?.find((w) => w.is_primary) || data?.[0];
      setActive(primary || null);
    }).catch(() => {});
  }, []);

  const loadMembers = useCallback(async () => {
    if (!active?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${active.id}/members`);
      setMembers(data?.members || []);
      setPending(data?.pending_invitations || []);
    } catch {
      setMembers([]); setPending([]);
    } finally {
      setLoading(false);
    }
  }, [active?.id]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const changeRole = async (agentId, role) => {
    try {
      await api.patch(`/workspaces/${active.id}/members/${agentId}`, { role });
      flash(`Role updated to ${ROLE_META[role].label}`);
      await loadMembers();
    } catch (e) {
      flash(e?.response?.data?.detail || "Could not change role");
    }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.agent_name} from this workspace? Their ${m.client_count} clients will be reassigned to you.`)) return;
    try {
      await api.delete(`/workspaces/${active.id}/members/${m.agent_id}`);
      flash(`${m.agent_name} removed`);
      await loadMembers();
    } catch (e) {
      flash(e?.response?.data?.detail || "Could not remove member");
    }
  };

  const cancelInvite = async (inv) => {
    try {
      await api.delete(`/workspaces/${active.id}/invitations/${inv.id}`);
      flash(`Invitation to ${inv.email} cancelled`);
      await loadMembers();
    } catch {
      flash("Could not cancel invitation");
    }
  };

  const copyInviteLink = (token) => {
    const url = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(url).then(
      () => flash("Invite link copied to clipboard"),
      () => flash("Copy failed — check console"),
    );
  };

  const isAdmin = active?.my_role === "admin";

  return (
    <Layout title="Team" subtitle="Settings · Workspace members" compact>
      <div className="max-w-5xl">
        {active && <AgencyLogoCard ws={active} canEdit={isAdmin} onChanged={(logo_url) => {
          setActive((a) => ({ ...a, logo_url }));
          setWorkspaces((ws) => ws.map((w) => (w.id === active.id ? { ...w, logo_url } : w)));
          invalidateCached("/workspaces/mine");
        }} />}
        {workspaces.length > 1 && (
          <div className="pm-card p-3 mb-4 flex items-center gap-3" data-testid="workspace-selector">
            <Users size={14} className="text-slate-500" />
            <span className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500">Workspace</span>
            <select
              value={active?.id || ""}
              onChange={(e) => setActive(workspaces.find((w) => w.id === e.target.value) || null)}
              className="pm-input h-9 text-sm flex-1 max-w-xs"
              data-testid="workspace-select"
            >
              {workspaces.map((w) => (
                <option key={w.id} value={w.id}>{w.name} ({w.my_role})</option>
              ))}
            </select>
          </div>
        )}

        {active && (
          <div className="pm-card overflow-hidden" data-testid="team-card">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center font-display font-black">
                {(active.name || "").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Workspace</div>
                <div className="font-display text-lg font-bold tracking-tight truncate">{active.name}</div>
                <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2">
                  <span>{members.length} members · {pending.length} pending</span>
                  <RolePill role={active.my_role} />
                </div>
              </div>
              {isAdmin && (
                <button
                  onClick={() => setInviteOpen(true)}
                  data-testid="invite-team-member-btn"
                  className="pm-btn-primary h-10 px-4 rounded-xl text-sm font-bold flex items-center gap-1.5"
                >
                  <UserPlus size={14} /> Invite team member
                </button>
              )}
            </div>

            {/* Members table */}
            <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 bg-slate-50/40">
              <div className="w-9" />
              <div className="flex-1 min-w-0">Name</div>
              <div className="w-44 hidden md:block">Email</div>
              <div className="w-24">Role</div>
              <div className="w-16 text-right">Clients</div>
              <div className="w-32 text-right">Status</div>
              <div className="w-20 text-right" />
            </div>

            {loading ? (
              <div className="p-8 text-center text-slate-500 text-sm">Loading team…</div>
            ) : members.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">No members yet.</div>
            ) : (
              members.map((m) => (
                <MemberRow
                  key={m.id}
                  m={m}
                  isAdmin={isAdmin}
                  isSelf={me?.id === m.agent_id}
                  onChangeRole={(r) => changeRole(m.agent_id, r)}
                  onRemove={() => removeMember(m)}
                />
              ))
            )}

            {/* Pending invitations */}
            {pending.length > 0 && (
              <>
                <div className="px-5 py-3 border-y border-slate-100 bg-amber-50/30 text-[10px] uppercase tracking-[0.22em] font-bold text-amber-700">
                  Pending invitations ({pending.length})
                </div>
                {pending.map((inv) => (
                  <div key={inv.id} className="px-5 py-3 flex items-center gap-3 border-b border-slate-100" data-testid={`pending-invitation-${inv.id}`}>
                    <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
                      <Mail size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{inv.email}</div>
                      <div className="text-[11px] text-slate-500">Invited by {inv.invited_by_name || "—"} · {fmtRelative(inv.created_at)}</div>
                    </div>
                    <RolePill role={inv.role} />
                    <button
                      onClick={() => copyInviteLink(inv.token)}
                      className="pm-btn-secondary h-8 px-2.5 rounded-lg text-[11px] font-bold flex items-center gap-1"
                      data-testid={`copy-invite-${inv.id}`}
                      title="Copy invitation link"
                    >
                      <Copy size={11} /> Copy link
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => cancelInvite(inv)}
                        className="text-rose-600 hover:text-rose-800 p-1.5"
                        data-testid={`cancel-invite-${inv.id}`}
                        title="Cancel invitation"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {active && isAdmin && (
          <VisibilityCard
            workspace={active}
            onSaved={async (mode) => {
              setActive((a) => ({ ...a, client_visibility: mode }));
              setWorkspaces((ws) => ws.map((w) => (w.id === active.id ? { ...w, client_visibility: mode } : w)));
              flash(`Inbox visibility set to ${mode}`);
            }}
          />
        )}

        <p className="text-[11px] text-slate-500 mt-4 max-w-2xl flex items-start gap-1.5">
          <Info size={11} className="mt-0.5 shrink-0" />
          Admins can invite members and reassign clients. Agents can manage their own clients and the shared inbox. Viewers have read-only access. The workspace always needs at least one Admin.
        </p>
      </div>

      <InviteModal
        open={inviteOpen}
        workspaceId={active?.id}
        onClose={() => setInviteOpen(false)}
        onSent={async (result) => {
          flash(result?.delivery?.sent_via === "gmail"
            ? "Invitation email sent"
            : "Invitation created — copy the link from the Pending section");
          await loadMembers();
          setInviteOpen(false);
        }}
      />

      {toast && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[10001] bg-slate-900 text-white px-4 py-2 rounded-xl shadow-xl text-sm font-bold animate-[fadeUp_0.22s_ease-out]"
          data-testid="settings-team-toast"
        >
          {toast}
        </div>
      )}
    </Layout>
  );
}

function MemberRow({ m, isAdmin, isSelf, onChangeRole, onRemove }) {
  const [open, setOpen] = useState(false);
  const [reqPending, setReqPending] = useState(false);
  const requestConsent = async () => {
    if (reqPending) return;
    setReqPending(true);
    try {
      await api.post(`/workspaces/${m.workspace_id}/members/${m.agent_id}/request-consent`);
      // eslint-disable-next-line no-alert
      alert(`Consent request sent to ${m.agent_name}. They'll see it on their Profile & Privacy page.`);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Could not send consent request");
    } finally {
      setReqPending(false);
    }
  };
  return (
    <div className="px-5 py-3 flex items-center gap-3 border-b border-slate-100 hover:bg-slate-50/30 transition-colors" data-testid={`member-row-${m.agent_id}`}>
      <div className="w-9 h-9 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center font-bold text-xs">
        {initials(m.agent_name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm truncate flex items-center gap-1.5">
          {m.agent_name}
          {isSelf && <span className="text-[10px] text-emerald-700 font-bold">(you)</span>}
          <VisibilityBadge member={m} canRequest={isAdmin && !isSelf} onRequest={requestConsent} requesting={reqPending} />
        </div>
        <div className="text-[11px] text-slate-500 md:hidden truncate">{m.agent_email}</div>
      </div>
      <div className="w-44 hidden md:block text-[12px] text-slate-500 truncate">{m.agent_email}</div>
      <div className="w-24"><RolePill role={m.role} /></div>
      <div className="w-16 text-right text-sm font-mono-tab text-slate-700">{m.client_count ?? 0}</div>
      <div className="w-32 text-right text-[11px] text-slate-500">{fmtRelative(m.last_login_at)}</div>
      <div className="w-20 text-right relative">
        {isAdmin && !isSelf && (
          <button
            onClick={() => setOpen((v) => !v)}
            className="pm-btn-secondary h-7 px-2 rounded-lg text-[11px] font-bold flex items-center gap-0.5 ml-auto"
            data-testid={`member-menu-${m.agent_id}`}
          >
            … <ChevronDown size={10} />
          </button>
        )}
        {open && (
          <div className="absolute right-0 mt-1 z-30 pm-card w-44 p-1 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-2 py-1 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Change role</div>
            {["admin", "agent", "viewer"].map((r) => (
              <button
                key={r}
                onClick={() => { onChangeRole(r); setOpen(false); }}
                className={`w-full text-left text-sm px-2 py-1.5 rounded hover:bg-slate-50 flex items-center justify-between ${r === m.role ? "font-bold text-emerald-700" : ""}`}
                data-testid={`set-role-${m.agent_id}-${r}`}
              >
                <span>{ROLE_META[r].label}</span>
                {r === m.role && <Check size={12} className="text-emerald-700" />}
              </button>
            ))}
            <div className="border-t border-slate-100 my-1" />
            <button
              onClick={() => { onRemove(); setOpen(false); }}
              className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-rose-50 text-rose-700 flex items-center gap-1.5"
              data-testid={`remove-member-${m.agent_id}`}
            >
              <Trash2 size={11} /> Remove from team
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function VisibilityBadge({ member, canRequest, onRequest, requesting }) {
  const mode = member.inbox_visibility || "private";
  const consent = !!member.inbox_consent_given;
  const requested = !!member.inbox_consent_requested_at;
  // Private members get an amber alert; admins can request consent
  if (mode === "private") {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-800 border border-amber-200 text-[10px] font-bold"
        data-testid={`vis-badge-${member.agent_id}`}
        title="Private inbox — admin cannot see this agent's threads without consent"
      >
        <AlertTriangle size={9} />
        Private
        {canRequest && (
          <button
            onClick={onRequest}
            disabled={requesting || requested}
            data-testid={`request-consent-${member.agent_id}`}
            className="ml-1 px-1 py-0 rounded bg-amber-200 hover:bg-amber-300 text-amber-900 text-[9px] font-bold disabled:opacity-60 disabled:cursor-not-allowed"
            title={requested ? "Request already sent — waiting on agent" : "Send a consent request"}
          >
            {requesting ? "…" : requested ? "Pending" : "Request"}
          </button>
        )}
      </span>
    );
  }
  const tone = mode === "transparent"
    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
    : "bg-indigo-50 text-indigo-800 border-indigo-200";
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[10px] font-bold capitalize ${tone}`}
      data-testid={`vis-badge-${member.agent_id}`}
      title={consent ? "Inbox visible — consent granted" : "Inbox visible by member's own choice"}
    >
      {mode}
    </span>
  );
}

function RolePill({ role }) {
  const r = ROLE_META[role] || ROLE_META.agent;
  const Icon = r.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 h-6 rounded-full border text-[11px] font-bold ${r.tone}`} data-testid={`role-pill-${role}`}>
      <Icon size={10} /> {r.label}
    </span>
  );
}

function InviteModal({ open, workspaceId, onClose, onSent }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("agent");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) { setEmail(""); setRole("agent"); setError(null); setSending(false); }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setSending(true); setError(null);
    try {
      const { data } = await api.post(`/workspaces/${workspaceId}/invite`, { email: email.trim().toLowerCase(), role });
      onSent && onSent(data);
    } catch (err) {
      let msg = err?.response?.data?.detail;
      if (Array.isArray(msg) && msg[0]?.msg) msg = msg[0].msg;
      setError(typeof msg === "string" ? msg : "Could not send invitation");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
      data-testid="invite-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center"><UserPlus size={16} /></div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Invite</div>
            <div className="font-display text-lg font-bold tracking-tight">Add a team member</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-900 p-1" data-testid="invite-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Email</span>
            <input
              type="email"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="sofia@puresports.com"
              className="pm-input h-11 mt-1.5 w-full"
              data-testid="invite-email-input"
            />
          </label>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-1.5">Role</div>
            <div className="space-y-2">
              {[
                { id: "agent",  desc: "Manages their own clients · uses the shared inbox" },
                { id: "viewer", desc: "Read-only access to everything" },
                { id: "admin",  desc: "Full access · can invite & remove members" },
              ].map((r) => (
                <label key={r.id} className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors ${role === r.id ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200 hover:bg-slate-50"}`}>
                  <input
                    type="radio"
                    name="role"
                    value={r.id}
                    checked={role === r.id}
                    onChange={() => setRole(r.id)}
                    className="mt-1"
                    data-testid={`invite-role-${r.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm flex items-center gap-1.5">
                      <RolePill role={r.id} />
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{r.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          {error && (
            <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2 flex items-start gap-1.5" data-testid="invite-error">
              <AlertTriangle size={11} className="mt-0.5 shrink-0" /> {error}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center gap-2 bg-slate-50">
          <button type="button" onClick={onClose} className="pm-btn-secondary h-10 px-4 rounded-xl text-sm font-bold">Cancel</button>
          <div className="flex-1" />
          <button
            type="submit"
            disabled={sending || !email.trim()}
            className="pm-btn-primary h-10 px-5 rounded-xl text-sm font-bold flex items-center gap-1.5 disabled:opacity-50"
            data-testid="invite-submit"
          >
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
            Send invitation
          </button>
        </div>
      </form>
    </div>
  );
}

function initials(name) {
  return (name || "?").split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}


const VIS_OPTIONS = [
  {
    id: "shared",
    Icon: Globe,
    label: "Shared",
    summary: "Everyone in the workspace sees every inbox thread.",
    detail: "Best for tight-knit agencies where every agent can pitch in.",
  },
  {
    id: "hybrid",
    Icon: Layers,
    label: "Hybrid",
    summary: "Everyone can see threads, but each agent sees their own clients by default.",
    detail: "Agents get a 'Mine / All' toggle in the inbox. Recommended default.",
  },
  {
    id: "private",
    Icon: Lock,
    label: "Private",
    summary: "Agents only see threads for clients assigned to them. Admins see everything.",
    detail: "Best for agencies handling sensitive negotiations across separate books.",
  },
];

function VisibilityCard({ workspace, onSaved }) {
  const [mode, setMode] = useState(workspace?.client_visibility || "hybrid");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setMode(workspace?.client_visibility || "hybrid");
  }, [workspace?.id, workspace?.client_visibility]);

  const save = async (next) => {
    if (next === mode) return;
    setSaving(true);
    setMode(next);
    try {
      await api.patch(`/workspaces/${workspace.id}/settings`, { client_visibility: next });
      onSaved && onSaved(next);
    } catch (e) {
      // revert on failure
      setMode(workspace?.client_visibility || "hybrid");
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Could not change visibility mode");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pm-card overflow-hidden mt-5" data-testid="visibility-card">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-700 flex items-center justify-center">
          <Globe size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Inbox visibility</div>
          <div className="font-display text-lg font-bold tracking-tight">Who sees which inbox threads?</div>
        </div>
        {saving && <Loader2 size={14} className="animate-spin text-slate-400" />}
      </div>
      <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        {VIS_OPTIONS.map((o) => {
          const Icon = o.Icon;
          const active = mode === o.id;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => save(o.id)}
              disabled={saving}
              data-testid={`visibility-option-${o.id}`}
              className={`text-left p-3 rounded-xl border-2 transition-all relative ${
                active
                  ? "border-emerald-400 bg-emerald-50/40 shadow-sm"
                  : "border-slate-200 hover:border-slate-300 bg-white"
              } ${saving ? "opacity-70 cursor-wait" : ""}`}
            >
              {active && (
                <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                  <Check size={11} strokeWidth={3} />
                </span>
              )}
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                <Icon size={14} />
              </div>
              <div className="font-display text-sm font-bold text-slate-900">{o.label}</div>
              <div className="text-[11px] text-slate-600 mt-0.5 leading-tight">{o.summary}</div>
              <div className="text-[10px] text-slate-400 mt-1.5 leading-tight">{o.detail}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}


// ─────────── Agency logo ───────────
function AgencyLogoCard({ ws, canEdit, onChanged }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const base = process.env.REACT_APP_BACKEND_URL;
  const src = ws.logo_url ? (ws.logo_url.startsWith("http") ? ws.logo_url : `${base}${ws.logo_url}`) : null;

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/workspaces/${ws.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChanged(data.logo_url);
    } catch (e) {
      setErr(e?.response?.data?.detail || t("Upload mislukt"));
    } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true); setErr("");
    try { await api.delete(`/workspaces/${ws.id}/logo`); onChanged(null); }
    catch (e) { setErr(e?.response?.data?.detail || t("Verwijderen mislukt")); }
    finally { setBusy(false); }
  };

  return (
    <div className="pm-card p-4 mb-4 flex flex-wrap items-center gap-4" data-testid="agency-logo-card">
      <div className="w-16 h-16 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0" data-testid="agency-logo-preview">
        {src ? <img src={src} alt={ws.name} className="w-full h-full object-contain" /> : <ImageIcon size={22} className="text-slate-400" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{t("Agency")}</div>
        <div className="font-display text-lg font-bold tracking-tight truncate" data-testid="agency-name">{ws.name}</div>
        <div className="text-xs text-slate-500">{t("Logo verschijnt in het zijmenu en op rapporten. PNG, JPG, WEBP of GIF — max 5 MB, wordt verkleind tot 512 px.")}</div>
        {err && <div className="text-xs text-red-600 mt-1" data-testid="agency-logo-error">{err}</div>}
      </div>
      {canEdit && (
        <div className="flex items-center gap-2">
          <label className={`pm-btn-primary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5 cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`} data-testid="agency-logo-upload-btn">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {src ? t("Logo vervangen") : t("Logo uploaden")}
            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="agency-logo-input" />
          </label>
          {src && (
            <button onClick={remove} disabled={busy} className="pm-btn-secondary h-9 px-3 rounded-lg text-xs font-bold flex items-center gap-1.5" data-testid="agency-logo-remove-btn">
              <Trash2 size={13} /> {t("Verwijderen")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
